import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { findCatalogProduct } from 'featureSliced/shared/utils/findCatalogProduct'
import {
  findOutOfBoundsArticles,
  isQuantityRefusal,
} from 'featureSliced/shared/utils/quantityBounds'
import { getRefusedProductIds } from 'featureSliced/shared/utils/soldOut'
import { IBucketProduct } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { ToastsSlice } from 'featureSliced/shared/store/reducers/ToasterReducer'

// Both halves of the rmh-441 refusal UI. Nothing is removed or clamped — the guest edits
// the cart (decision 2026-10-16); the toast names the articles so they know what to fix.
const useQuantityToast = () => {
  const t = useTranslations()
  const dispatch = useMyDispatch()
  const categories = useMySelector((state) => state.categories?.items || [])
  // same lookup useGetTranslatedNames does, read defensively — this hook also mounts in
  // trees whose stores carry no translations (tests, early boot)
  const selectedLanguage = useMySelector(
    (state) => state.common?.selectedLanguage
  )
  const translations = useMySelector((state) => state.translations?.items)

  return (productIds: number[]) => {
    const flatProducts = categories.flatMap((category) => category.products)
    const names = productIds
      .map((productId) => {
        const product = findCatalogProduct(flatProducts, productId)
        // a refused id missing from the catalog still gets named — by number —
        // so the toast never ends in a bare colon
        if (!product) return `#${productId}`
        const translated =
          translations?.[selectedLanguage as string]?.productTitles?.[
            product.id
          ]
        return typeof translated === 'string' ? translated : product.title || ''
      })
      .filter(Boolean)
      .join(', ')
    dispatch(
      ToastsSlice.actions.openToast({
        message: t('products.quantityOutOfBounds', { products: names }) || '',
        type: 'warning',
      })
    )
  }
}

// Proactive checkpoint before an order/payment request: true = blocked (toast shown).
// Bounds come from the LIVE catalog, so carts persisted before this fix (or before an
// admin raised a bound) are judged correctly.
export const useQuantityBoundsCheck = () => {
  const categories = useMySelector((state) => state.categories?.items || [])
  const toast = useQuantityToast()

  return (lines: IBucketProduct[] = []): boolean => {
    const flatProducts = categories.flatMap((category) => category.products)
    const violations = findOutOfBoundsArticles(lines, (productId) =>
      findCatalogProduct(flatProducts, productId)
    )
    if (!violations.length) return false
    toast(violations.map((violation) => violation.productId))
    return true
  }
}

// Maps the server's 400 QUANTITY_OUT_OF_BOUNDS (quantityOrderGate) — the backstop for a
// stale bundle that skipped the proactive check. Same toast, nothing removed.
export const useQuantityRefusal = () => {
  const toast = useQuantityToast()

  return (error: unknown): boolean => {
    if (!isQuantityRefusal(error)) return false
    toast(getRefusedProductIds(error))
    return true
  }
}
